<template>
    <div v-if="vehicle" class="booking-request-form">
        <div class="booking-request-form__header-section">
            <LoadingIndicator v-if="isLoadingPrice" class="booking-request-form__loading-indicator" />
            <template v-else>
                <i18n
                    v-if="selectedDates.from"
                    class="booking-request-form__price"
                    path="pricing.x_per_night"
                    tag="div"
                >
                    <template #price>
                        <strong class="booking-request-form__price-value">{{ nightlyPrice }}</strong>
                    </template>
                </i18n>
                <i18n v-else class="booking-request-form__price" path="pricing.from_x_per_night" tag="div">
                    <template #price>
                        <strong class="booking-request-form__price-value">{{ nightlyPrice }}</strong>
                    </template>
                </i18n>

                <template v-if="!selectedDates.from">
                    <span v-if="includedKilometers === UNLIMITED_KILOMETERS" class="booking-request-form__pill">
                        {{ $t('booking_request_form.unlimited_kilometers') }}
                    </span>
                    <strong v-else>
                        {{
                            $t('booking_request_form.kilometers_included_per_night', { kilometers: includedKilometers })
                        }}
                    </strong>

                    <div v-if="vehicle.hasPlatformInsurance" class="booking-request-form__platform-insurance">
                        <span>
                            {{ $t('common.insurance_by') }}
                        </span>
                        <InsuranceLogo
                            :vendor="vehicle.insuranceVendor"
                            class="booking-request-form__platform-insurance-logo"
                            :class="{
                                'booking-request-form__platform-insurance-logo__omocom': isOmocomInsurance,
                            }"
                        />
                    </div>
                    <span v-else>
                        {{ $t('booking_request_form.owners_insurance') }}
                    </span>
                </template>
            </template>
        </div>

        <div v-if="renterCanNotBookBaloiseVehicle" class="booking-request-form__content">
            <Notice type="warning">
                {{ $t('pages.booking_request.baloise_vehicle_renting_disallowed_notice') }}
            </Notice>
            <button
                class="button button--primary booking-request-form__book-button button--full-width"
                @click="applyFilter"
            >
                {{ $t('modals.insurance_limitation.show_available_vehicles') }}
            </button>
        </div>
        <div v-else class="booking-request-form__content">
            <div class="booking-request-form__dates">
                <label class="label">
                    <template v-if="selectedDates.from">
                        {{ $t('booking_request_form.dates_selected_label') }}
                    </template>
                    <template v-else>
                        {{ $t('booking_request_form.date_select_label') }}
                    </template>
                </label>
                <DateSelectInput
                    :init-from="selectedDates.from"
                    :init-to="selectedDates.to"
                    :vehicle="vehicle"
                    :disabled="!featureEnabled('renterAccess')"
                    :disabled-dates="vehicle.unavailableDates"
                    :high-season-min-booking-length="vehicle.minBookingLengthHigh"
                    :low-season-min-booking-length="vehicle.minBookingLengthLow"
                    :has-tooltips="true"
                    :placeholder="$t('booking_request_form.date_select_placeholder')"
                    :add-to-query-immediately="true"
                    :show-date-range-icon="false"
                    :compact-style="true"
                />
                <div
                    v-if="selectedDates.from && selectedDates.to && $v.selectedDates.$invalid"
                    class="booking-request-form__error"
                >
                    <Notice v-if="!$v.selectedDates.from.validRange || !$v.selectedDates.to.validRange" type="warning">
                        {{ $t('booking_request_form.error_invalid_range') }}
                    </Notice>
                    <Notice v-if="!$v.selectedDates.from.checkinAvailable" type="warning">
                        {{ $t('booking_request_form.error_checkin_not_available') }}
                    </Notice>
                    <Notice v-if="!$v.selectedDates.to.checkoutAvailable" type="warning">
                        {{ $t('booking_request_form.error_checkout_not_available') }}
                    </Notice>
                    <Notice v-if="!$v.selectedDates.from.minLength || !$v.selectedDates.to.minLength" type="warning">
                        {{ $t('booking_request_form.error_too_short', { minLength: minBookingLengthForDates }) }}
                    </Notice>
                    <Notice v-if="!$v.selectedDates.from.maxLength || !$v.selectedDates.to.maxLength" type="warning">
                        {{ $t('booking_request_form.error_too_long', { maxLength: 90 }) }}
                    </Notice>
                </div>
            </div>

            <template v-if="!$v.selectedDates.$invalid">
                <VehiclePricing
                    class="booking-request-form__price-info"
                    :vehicle="vehicle"
                    :show-deposit="vehicle.pricing ? !vehicle.pricing.cashDeposit : false"
                    :show-num-of-nights="true"
                    :is-loading="isLoadingPrice"
                    :load-pricing-error="loadPricingError"
                    :pricing="vehicle.pricing"
                    :from-date="selectedDates.from"
                    :to-date="selectedDates.to"
                />

                <template v-if="!isLoadingPrice">
                    <span v-if="includedKilometers === UNLIMITED_KILOMETERS" class="booking-request-form__pill">
                        {{ $t('booking_request_form.unlimited_kilometers') }}
                    </span>
                    <strong v-else>
                        {{ $t('booking_request_form.kilometers_included', { kilometers: includedKilometers }) }}
                    </strong>
                </template>
            </template>

            <Notice v-if="notAllowedToRentError" type="warning" class="booking-request-form__disabled-message">
                {{ notAllowedToRentError }}
            </Notice>

            <div v-if="!isLoggedIn || isRenter" id="booking-form-btn-wrapper">
                <button
                    v-if="selectedDates.from"
                    :disabled="$v.selectedDates.$invalid || !featureEnabled('renterAccess')"
                    class="button button--primary booking-request-form__book-button button--full-width"
                    @click="$emit('click')"
                >
                    <template v-if="vehicle.bookingMethod === 'instant'">
                        {{ instantBookingCtaLabel }}
                    </template>
                    <template v-else>{{ bindingBookingCtaLabel }}</template>
                </button>
                <button
                    v-else
                    class="button button--primary booking-request-form__book-button button--full-width"
                    @click="$root.$emit('focus-datepicker')"
                >
                    {{ $t('booking_request_form.select_dates_button') }}
                </button>
            </div>

            <Notice v-if="isOwner" :show-icon="true" type="info" class="booking-request-form__disabled-message">
                {{ $t('booking_request_form.owner_notice') }}
            </Notice>
            <RenterAccessSignupForm v-else-if="!featureEnabled('renterAccess')" :country="vehicle.country" />
            <div v-else class="booking-request-form__footnote">
                {{ $t('booking_request_form.footnote_not_binding') }}<br />
            </div>
            <div class="booking-request-form__payment-method-images">
                <img
                    v-for="image in paymentMethodImages"
                    :key="image.alt"
                    class="booking-request-form__payment-method-image"
                    :class="{
                        'booking-request-form__payment-method-image__visa': image.alt === 'Visa',
                    }"
                    :src="image.src"
                    :alt="image.alt"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

import { getFilterValue, buildQuery } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN, RENTABLE_OUTSIDE_OF_SWITZERLAND } from '~/config/filterKeys';
import { OMOCOM } from '~/config/insurance';
import posthogMixin from '~/mixins/posthogMixin';
import DateSelectInput from '~/components/form-elements/DateSelectInput.vue';
import Notice from '~/components/Notice.vue';
import VehiclePricing from '~/components/vehicles/VehiclePricing.vue';
import RenterAccessSignupForm from '~/components/onboarding/RenterAccessSignupForm.vue';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import InsuranceLogo from '~/components/InsuranceLogo.vue';

export default {
    components: {
        DateSelectInput,
        Notice,
        VehiclePricing,
        RenterAccessSignupForm,
        LoadingIndicator,
        InsuranceLogo,
    },
    mixins: [validationMixin, posthogMixin],
    validations: {
        // NOTE: This code is duplicated in the BookingRequest view
        selectedDates: {
            from: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                checkinAvailable() {
                    return this.$validators.isCheckinAvailable(this.vehicle, this.selectedDates.from);
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
            to: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                checkoutAvailable() {
                    return this.$validators.isCheckoutAvailable(this.vehicle, this.selectedDates.to);
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
        },
    },
    props: {
        vehicle: {
            type: Object,
            default: null,
        },
        isLoadingPrice: {
            type: Boolean,
            default: false,
        },
        loadPricingError: {
            type: Boolean,
            default: false,
        },
        notAllowedToRentError: {
            type: String,
            default: null,
        },
        renterCanNotBookBaloiseVehicle: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            UNLIMITED_KILOMETERS: -1,
            OMOCOM,
        };
    },
    computed: {
        ...mapGetters(['user', 'isLoggedIn', 'isOwner', 'isRenter']),
        isOmocomInsurance() {
            return this.vehicle.insuranceVendor?.name?.toLowerCase() === OMOCOM;
        },
        instantBookingCtaLabel() {
            return this.isExperimentEnabled('2025-06-booking-request-cta')
                ? this.$t('booking_request_form.book_cta')
                : this.$t('booking_request_form.book_instantly');
        },
        bindingBookingCtaLabel() {
            return this.isExperimentEnabled('2025-06-booking-request-cta')
                ? this.$t('booking_request_form.book_cta')
                : this.$t('booking_request_form.send_request_button');
        },
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        includedKilometers() {
            if (this.vehicle.includedDistance !== this.UNLIMITED_KILOMETERS) {
                const multiplier = this.vehicle.pricing?.numberOfNights || 1;

                return Math.ceil(this.vehicle.includedDistance * multiplier);
            }

            return this.vehicle.includedDistance;
        },
        paymentMethodImages() {
            // Duplicate of bookingProcessMixin.js. We probably need a new mixin for payment related methods
            if (!this.vehicle.country) {
                return [];
            }

            const ALL_COUNTRIES = 'all';
            const allowedCountries = {
                Twint: ['CH'],
                Swish: ['SE'],
                'Mobile Pay': ['DK', 'FI', 'NO'],
                'Apple Pay': ALL_COUNTRIES,
                'Google Pay': ALL_COUNTRIES,
                Visa: ALL_COUNTRIES,
                Mastercard: ALL_COUNTRIES,
                Eps: ['AT'],
            };

            const paymentMethods = [
                { src: require('~/assets/images/payment-methods/twint.svg'), alt: 'Twint' },
                { src: require('~/assets/images/payment-methods/swish.svg'), alt: 'Swish' },
                { src: require('~/assets/images/payment-methods/mobile-pay.svg'), alt: 'Mobile Pay' },
                { src: require('~/assets/images/payment-methods/apple-pay.svg'), alt: 'Apple Pay' },
                { src: require('~/assets/images/payment-methods/google-pay.svg'), alt: 'Google Pay' },
                { src: require('~/assets/images/payment-methods/visa.svg'), alt: 'Visa' },
                { src: require('~/assets/images/payment-methods/mastercard.svg'), alt: 'Mastercard' },
                { src: require('~/assets/images/payment-methods/eps.svg'), alt: 'Eps' },
            ];

            return paymentMethods.filter(method => {
                const supportedCountries = allowedCountries[method.alt];

                return (
                    supportedCountries === ALL_COUNTRIES ||
                    supportedCountries.includes(this.vehicle?.country.toUpperCase())
                );
            });
        },
        nightlyPrice() {
            const { formatAsPrice, price } = this.$options.filters;
            let nightlyPrice;

            if (this.selectedDates.from) {
                nightlyPrice = this.vehicle.pricing.totalPrice / this.vehicle.pricing.numberOfNights;
            } else {
                nightlyPrice = this.vehicle.pricing.lowNightlyPrice;
            }

            const nightlyPriceFormatted = formatAsPrice(price(nightlyPrice), this.vehicle.pricing.currency, 0);

            return nightlyPriceFormatted;
        },
        minBookingLengthForDates() {
            // NOTE: This code is duplicated in the BookingRequest view
            const { from, to } = this.selectedDates;

            if (this.$utils.isOnlyHighSeason(from, to)) {
                return this.vehicle.minBookingLengthHigh;
            }

            if (this.$utils.isOnlyLowSeason(from, to)) {
                return this.vehicle.minBookingLengthLow;
            }

            // Mixed season, take the "loosest" rule
            return Math.min(this.vehicle.minBookingLengthHigh, this.vehicle.minBookingLengthLow);
        },
    },
    methods: {
        applyFilter() {
            const query = buildQuery(this.$route.query, { [RENTABLE_OUTSIDE_OF_SWITZERLAND]: true });

            this.$router.push(this.localePath({ path: this.$utils.getVehicleSearchPagePath(this.vehicle), query }));
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';
@import '@/sass/blocks/_form.scss';

.booking-request-form {
    box-sizing: border-box;
    width: 100%;
    text-align: left;
    border: 1px solid $gray;
    border-radius: $default-border-radius;

    &__header-section {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 24px;
        border-bottom: 1px solid $gray;
    }

    &__pill {
        width: fit-content;
        padding: 4px 12px;
        border-radius: 100px;
        font-weight: 600;
        background: $tertiary-lightest;
        @include font-size(14px);
    }

    &__content {
        padding: 24px;
    }

    &__price {
        @include font-size(14px);
    }

    &__price-value {
        @include font-size(20px);
    }

    &__platform-insurance {
        display: flex;
        align-items: center;
        gap: 4px;

        span {
            @include font-size(14px);
        }
    }

    &__platform-insurance-logo {
        display: flex;
        width: 70px;
        height: 14px;
        margin-top: -1px;

        &__omocom {
            height: 26px;
            width: auto;
            margin-top: -14px;
        }
    }

    &__loading-indicator {
        text-align: left;
    }

    &__dates {
        label {
            margin-bottom: 8px;
            font-weight: normal;
            @include font-size(13px);
        }
    }

    &__payment-method-images {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        margin-top: 16px;
    }

    &__payment-method-image {
        max-height: 20px;

        &__visa {
            max-width: 40px;
        }
    }

    &__error {
        margin-top: 10px;
    }

    &__price-info {
        margin: 32px auto 16px;
    }

    &__book-button {
        margin-top: 32px;
        box-sizing: border-box;
    }

    .renter-access-signup {
        margin-top: 20px;
    }

    &__disabled-message {
        margin-top: 20px;
    }

    &__footnote {
        @include font-size(13px);
        text-align: center;
        margin-top: 32px;
        line-height: 1.5em;
    }
}
</style>
